import { useEffect, useState } from 'react';
import { formatMoney, parseAmount } from '@domain';
import { loadCostOptions, type CostDetail, type CostOption, type DataSource } from '../api.js';
import type { Translate } from '../i18n/index.js';
import { SearchableSelect, type SelectOption } from '../components/SearchableSelect.js';
import { theme } from '../theme.js';
import { Card } from '../components/ui.js';

/**
 * What this course cost.
 *
 * Revenue linked itself — every invoice for a cohort was raised here. Costs
 * did not: a supplier bill arrives through the purchases screens and has no
 * idea a course exists. This is where somebody says which bills belong to
 * which course, and it is the other half of knowing what a course made.
 *
 * Only a link is stored. The supplier, the amount and the status are read
 * from Xero each time, so a bill later corrected or voided is corrected
 * here too without anybody remembering to come back.
 */

export function DeliveryCosts({
  deliveryId,
  costs,
  currency,
  locked,
  busy,
  source,
  t,
  onLink,
  onUnlink,
  onNewBill,
}: {
  deliveryId: number;
  costs: CostDetail[];
  currency: string;
  /** The cohort is closed. Nothing more goes on it. */
  locked: boolean;
  busy: boolean;
  source: DataSource;
  t: Translate;
  onLink: (cost: {
    source: 'bill' | 'spend';
    xeroId: string;
    allocatedAmount: number | null;
    note: string | null;
  }) => void;
  onUnlink: (costId: number) => void;
  /** File a brand-new bill, already on this course. */
  onNewBill: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [options, setOptions] = useState<CostOption[]>([]);
  const [chosen, setChosen] = useState('');
  const [share, setShare] = useState('');
  const [note, setNote] = useState('');

  /*
    Fetched when the panel opens, not with the page. Listing every bill in
    the company costs two Xero calls, and most visits to a delivery are to
    look at it rather than to attach a cost.
  */
  useEffect(() => {
    if (!adding) return;
    let cancelled = false;
    loadCostOptions(source)
      .then((loaded) => !cancelled && setOptions(loaded.costs))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [adding, source, deliveryId]);

  const key = (cost: { source: string; xeroId: string }) => `${cost.source}:${cost.xeroId}`;
  const picked = options.find((option) => key(option) === chosen) ?? null;

  const selectOptions: SelectOption[] = options.map((option) => ({
    value: key(option),
    label: [
      option.date ?? '',
      option.supplier || option.description,
      // The document's own currency, not the company's. A USD bill shown
      // as QAR is the mistake this whole column exists to stop.
      `${formatMoney(option.subTotal)} ${option.currencyCode || currency}`,
      option.usedElsewhere ? t('(already on another course)') : '',
    ]
      .filter(Boolean)
      .join('  ·  '),
    group: option.source === 'bill' ? t('Bills') : t('Money out'),
  }));

  const cell = {
    padding: '9px 12px',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.dividerSoft}`,
  };

  const heading = {
    padding: '9px 12px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '.04em',
    color: theme.label,
    borderBottom: `1px solid ${theme.border}`,
    textAlign: 'start' as const,
  };

  const field = {
    height: '34px',
    width: '100%',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 10px',
    fontSize: '13px',
    background: '#fff',
  };

  const label = { display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' };

  return (
    <Card style={{ marginBottom: '14px' }}>
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${theme.dividerSoft}`,
          fontWeight: 600,
          fontSize: '13px',
          background: theme.surfaceHeader,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <span>{t('What this course cost')}</span>
        {!locked && !adding && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {/*
              Two ways in, because a cost arrives in two ways.

              A bill that already exists in Xero gets attached — that is
              the picker beside this. A bill that does not exist yet gets
              filed, and this takes you to the ordinary bill form with the
              course already chosen, so it comes back linked without
              anybody having to remember to link it.

              It is the same form, not a small one of its own: a bill needs
              a supplier, a date, accounts, tax and lines, and a cut-down
              version here would be a second place to get all of that
              wrong.
            */}
            <button
              type="button"
              onClick={onNewBill}
              style={{
                background: theme.nav,
                color: '#fff',
                border: 0,
                borderRadius: theme.radiusControl,
                padding: '6px 13px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + {t('New bill for this course')}
            </button>
            <button
              type="button"
              onClick={() => setAdding(true)}
              style={{
                background: 'transparent',
                border: `1px solid ${theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                padding: '6px 13px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('Attach one that exists')}
            </button>
          </div>
        )}
      </div>

      {adding && (
        <div style={{ padding: '16px', borderBottom: `1px solid ${theme.dividerSoft}` }}>
          <label style={{ display: 'block', marginBottom: '12px' }}>
            <span style={label}>{t('Bill or money out')}</span>
            <SearchableSelect
              value={chosen}
              options={selectOptions}
              placeholder={t('Search your bills…')}
              emptyLabel={t('No bills or money out found in Xero')}
              onChange={setChosen}
              t={t}
            />
          </label>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
            {/*
              The share, for a bill covering more than one course — a
              printer's invoice for three sets of handouts. Blank means all
              of it, which is the ordinary case and should not have to be
              typed.
            */}
            <label style={{ flex: '0 1 200px', minWidth: 0 }}>
              <span style={label}>
                {t('Share for this course')}{' '}
                <span style={{ fontWeight: 400, color: theme.textMuted }}>
                  ({t('blank = all of it')})
                </span>
              </span>
              <input
                value={share}
                onChange={(e) => setShare(e.target.value)}
                inputMode="decimal"
                dir="ltr"
                placeholder={picked ? formatMoney(picked.subTotal) : ''}
                style={{ ...field, fontFamily: theme.fontMono }}
              />
            </label>

            <label style={{ flex: '1 1 260px', minWidth: 0 }}>
              <span style={label}>
                {t('Note')}{' '}
                <span style={{ fontWeight: 400, color: theme.textMuted }}>({t('optional')})</span>
              </span>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('Why only part of it, for example')}
                style={field}
              />
            </label>
          </div>

          <div style={{ display: 'flex', gap: '9px' }}>
            <button
              type="button"
              disabled={picked === null || busy}
              onClick={() => {
                if (!picked) return;
                onLink({
                  source: picked.source,
                  xeroId: picked.xeroId,
                  allocatedAmount: share.trim() === '' ? null : parseAmount(share),
                  note: note.trim() === '' ? null : note.trim(),
                });
                setAdding(false);
                setChosen('');
                setShare('');
                setNote('');
              }}
              style={{
                height: '36px',
                minWidth: '130px',
                background: picked && !busy ? theme.nav : theme.borderStrong,
                color: '#fff',
                border: 0,
                borderRadius: theme.radiusControl,
                fontSize: '13px',
                fontWeight: 600,
                cursor: picked && !busy ? 'pointer' : 'default',
              }}
            >
              {t('Add the cost')}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                background: 'transparent',
                border: `1px solid ${theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      )}

      {costs.length === 0 ? (
        <div style={{ padding: '16px', fontSize: '13px', color: theme.textMuted }}>
          {t('Nothing has been put on this course yet. Its costs will not appear until they are.')}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={heading}>{t('What')}</th>
              <th style={heading}>{t('Supplier')}</th>
              <th style={heading}>{t('Date')}</th>
              <th style={{ ...heading, textAlign: 'end' }}>{t('On this course')}</th>
              <th style={{ ...heading, width: '90px' }} />
            </tr>
          </thead>
          <tbody>
            {costs.map((cost) => (
              <tr key={cost.costId} style={{ opacity: cost.missing ? 0.5 : 1 }}>
                <td style={cell}>
                  {cost.description || (cost.source === 'bill' ? t('Bill') : t('Money out'))}
                  {cost.note && (
                    <div style={{ color: theme.textMuted, fontSize: '11.5px' }}>{cost.note}</div>
                  )}
                  {cost.missing && (
                    <div style={{ color: theme.warnDeep, fontSize: '11.5px', fontWeight: 600 }}>
                      {t('Xero no longer has this. It is not counted.')}
                    </div>
                  )}
                  {/*
                    A warning, never a refusal. Sometimes the share is right
                    and the bill in Xero is the thing that is wrong, and the
                    person holding the paperwork is the one who can tell.
                  */}
                  {/*
                    Each figure is labelled rather than spliced into a
                    sentence. A clause built by joining translated fragments
                    around a number reads backwards in Arabic even when
                    every word in it is right.
                  */}
                  {cost.overAllocated && (
                    <div style={{ color: theme.warnDeep, fontSize: '11.5px', fontWeight: 600 }}>
                      <div>{t('More has been put on courses than this bill is worth.')}</div>
                      <div style={{ fontWeight: 400 }}>
                        {t('Put on courses')}:{' '}
                        <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
                          {formatMoney(cost.claimedTotal)}
                        </span>
                        {' · '}
                        {t('The bill itself')}:{' '}
                        <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
                          {formatMoney(cost.subTotal)}
                        </span>
                      </div>
                    </div>
                  )}
                  {!cost.overAllocated && cost.onOtherCourses > 0 && (
                    <div style={{ color: theme.textMuted, fontSize: '11.5px' }}>
                      {t('Also on other courses')}: {cost.onOtherCourses}
                    </div>
                  )}
                </td>
                <td style={{ ...cell, color: theme.textMuted }}>{cost.supplier || '—'}</td>
                <td style={{ ...cell, fontFamily: theme.fontMono, fontSize: '12px' }} dir="ltr">
                  {cost.date ?? '—'}
                </td>
                <td
                  style={{
                    ...cell,
                    textAlign: 'end',
                    fontFamily: theme.fontMono,
                    direction: 'ltr',
                  }}
                >
                  {formatMoney(cost.allocatedAmount ?? cost.subTotal)}
                  {/* Only worth saying when it is a share of something
                      bigger; otherwise it repeats the figure beside it. */}
                  {cost.allocatedAmount !== null && cost.allocatedAmount !== cost.subTotal && (
                    <div style={{ color: theme.textMuted, fontSize: '11px' }}>
                      {t('of')} {formatMoney(cost.subTotal)}
                    </div>
                  )}
                  {/* Named only when it is not the company's own, where it
                      is the one thing that stops the figure being added. */}
                  {cost.currencyCode !== '' && cost.currencyCode !== currency && (
                    <div style={{ color: theme.warnDeep, fontSize: '11px', fontWeight: 600 }}>
                      {cost.currencyCode}
                    </div>
                  )}
                </td>
                <td style={{ ...cell, textAlign: 'end' }}>
                  {!locked && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onUnlink(cost.costId)}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${theme.border}`,
                        borderRadius: theme.radiusControl,
                        padding: '5px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: busy ? 'default' : 'pointer',
                      }}
                    >
                      {t('Remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ padding: '11px 16px', fontSize: '11.5px', color: theme.textMuted, lineHeight: 1.6 }}>
        {t('Amounts exclude tax. The bill stays in Xero exactly as it is — this only says which course it belongs to.')}
      </div>
    </Card>
  );
}
